// frontend\src\components\Requirements\CategoryManagement.js

import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Badge, Row, Col, Modal, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTrash, faPlus, faCheck, faTimes, 
  faHome, faUtensils, faCar, faShoppingCart, 
  faPlane, faGift, faHeart, faStar,
  faBuilding, faMoneyBill, faCreditCard,
  faGraduationCap, faBriefcase, faLaptop,
  faMobile, faTv, faGamepad, faMusic,
  faBook, faNewspaper, faCamera,
  faPaw, faTree, faBicycle, faBus,
  faTrain, faShip, faMotorcycle, faTaxi,
  faSubway, faWalking, faRunning,
  faDumbbell, faPlug, faWifi, faPiggyBank,
  faHammer, faTshirt, faSoccerBall, faSeedling,
  faTools, faPaintBrush
} from '@fortawesome/free-solid-svg-icons';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../services/CategoryService';
import { handleError } from '../../utils/errorHandling';
import { sanitizeInput } from '../../utils/sanitize';

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCategory, setNewCategory] = useState({
    name: '',
    description: '',
    color: '#0d6efd', // Default Bootstrap primary color
    icon: ''
  });
  const [showIconModal, setShowIconModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedIcon, setSelectedIcon] = useState(null);

  // Function to get contrasting text color based on background color
  const getContrastColor = (hexColor) => {
    // Remove the # if present
    const color = hexColor.replace('#', '');
    
    // Convert hex to RGB
    const r = parseInt(color.substr(0, 2), 16);
    const g = parseInt(color.substr(2, 2), 16);
    const b = parseInt(color.substr(4, 2), 16);
    
    // Calculate brightness using the formula: (0.299*R + 0.587*G + 0.114*B)
    const brightness = (0.299 * r + 0.587 * g + 0.114 * b);
    
    // Return white or black based on brightness
    return brightness > 128 ? '#000000' : '#FFFFFF';
  };

  // Add this function to stop event propagation for modal inputs
  const stopPropagation = (e) => {
    e.stopPropagation();
  };

  const iconOptions = [
    { value: 'home', icon: faHome, label: 'House' },
    { value: 'car', icon: faCar, label: 'Car' },
    { value: 'utensils', icon: faUtensils, label: 'Food/Dining' },
    { value: 'shopping-cart', icon: faShoppingCart, label: 'Shopping' },
    { value: 'medical', icon: faHeart, label: 'Healthcare' },
    { value: 'dumbbell', icon: faDumbbell, label: 'Fitness' },
    { value: 'graduation-cap', icon: faGraduationCap, label: 'Education' },
    { value: 'plug', icon: faPlug, label: 'Utilities' },
    { value: 'wifi', icon: faWifi, label: 'Internet' },
    { value: 'mobile-alt', icon: faMobile, label: 'Phone' },
    { value: 'tv', icon: faTv, label: 'Entertainment' },
    { value: 'plane', icon: faPlane, label: 'Travel' },
    { value: 'paw', icon: faPaw, label: 'Pets' },
    { value: 'gift', icon: faGift, label: 'Gifts' },
    { value: 'piggy-bank', icon: faPiggyBank, label: 'Savings' },
    { value: 'credit-card', icon: faCreditCard, label: 'Credit Card' },
    { value: 'money-bill', icon: faMoneyBill, label: 'Bills' },
    { value: 'hammer', icon: faHammer, label: 'Maintenance' },
    { value: 'tshirt', icon: faTshirt, label: 'Clothing' },
    { value: 'bus', icon: faBus, label: 'Transport' },
    { value: 'soccer-ball', icon: faSoccerBall, label: 'Sport' },
    { value: 'laptop', icon: faLaptop, label: 'Technology' },
    { value: 'seedling', icon: faSeedling, label: 'Gardening' },
    { value: 'tools', icon: faTools, label: 'Renovations' },
    { value: 'paint-brush', icon: faPaintBrush, label: 'Hobby' }
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await getCategories();
      // Filter out the 'uncategorized' category
      const filteredCategories = data.filter(category => category.name.toLowerCase() !== 'uncategorized');
      setCategories(filteredCategories);
      setLoading(false);
    } catch (error) {
      setError(handleError(error, 'CategoryManagement'));
      setLoading(false);
    }
  };

  const handleCellClick = (categoryId, field) => {
    setEditingCell({ categoryId, field });
  };

  const handleCellChange = (e, categoryId) => {
    const { name, value } = e.target;
    setCategories(prev => prev.map(category => 
      category.id === categoryId 
        ? { ...category, [name]: sanitizeInput(value) }
        : category
    ));
  };

  const handleSave = async (categoryId) => {
    try {
      const category = categories.find(c => c.id === categoryId);
      await updateCategory(categoryId, {
        name: category.name,
        description: category.description,
        color: category.color,
        icon: category.icon
      });
      setEditingCell(null);
    } catch (error) {
      setError(handleError(error, 'CategoryManagement'));
      fetchCategories(); // Refresh to get original values
    }
  };

  const handleCancel = (categoryId) => {
    setEditingCell(null);
    fetchCategories(); // Refresh to get original values
  };

  const handleDelete = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await deleteCategory(categoryId);
        fetchCategories();
      } catch (error) {
        setError(handleError(error, 'CategoryManagement'));
      }
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      setError('Category name is required');
      return;
    }

    try {
      await createCategory(newCategory);
      setNewCategory({
        name: '',
        description: '',
        color: '#0d6efd', // Default Bootstrap primary color
        icon: ''
      });
      setShowAddModal(false);
      fetchCategories();
    } catch (error) {
      setError(handleError(error, 'CategoryManagement'));
    }
  };

  const handleIconSelect = (iconValue) => {
    // Ensure iconValue is a string
    setSelectedIcon(String(iconValue));
  };

  const handleIconConfirm = async () => {
    if (selectedCategory && selectedIcon) {
      try {
        if (selectedCategory.id === 'new') {
          setNewCategory(prev => ({ ...prev, icon: selectedIcon }));
        } else {
          // Get the current category data
          const currentCategory = categories.find(c => c.id === selectedCategory.id);
          if (!currentCategory) {
            throw new Error('Category not found');
          }

          // Create updated category with all fields from current category
          const updatedCategory = {
            name: currentCategory.name,
            description: currentCategory.description || '',
            color: currentCategory.color,
            icon: selectedIcon // This should be a string matching one of the ICON_CHOICES
          };

          // Update the category
          const response = await updateCategory(selectedCategory.id, updatedCategory);
          
          // Update local state
          setCategories(prev => prev.map(category => 
            category.id === selectedCategory.id 
              ? { ...category, icon: selectedIcon }
              : category
          ));
        }
        setShowIconModal(false);
        setSelectedCategory(null);
        setSelectedIcon(null);
      } catch (error) {
        if (error.response) {
          console.error('Error response:', error.name);
        }
        setError(handleError(error, 'CategoryManagement'));
        // Refresh categories to get the original values
        fetchCategories();
      }
    }
  };

  const renderIconGrid = () => {
    return (
      <div className="icon-grid" style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fill, minmax(50px, 1fr))', 
        gap: '1.5rem',
        padding: '1.5rem',
        justifyContent: 'center'
      }}>
        {iconOptions.map(option => (
          <div
            key={option.value}
            onClick={() => handleIconSelect(option.value)}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'pointer',
              padding: '0.5rem',
              transition: 'all 0.2s ease-in-out',
              backgroundColor: selectedIcon === option.value ? '#e9ecef' : 'transparent',
              borderRadius: '8px'
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <FontAwesomeIcon 
              icon={option.icon} 
              style={{ 
                fontSize: '1.8em',
                color: '#495057'
              }} 
            />
          </div>
        ))}
      </div>
    );
  };

  const colorOptions = [
    { value: '#0d6efd', label: 'Blue' },
    { value: '#6c757d', label: 'Gray' },
    { value: '#198754', label: 'Green' },
    { value: '#dc3545', label: 'Red' },
    { value: '#ffc107', label: 'Yellow' },
    { value: '#0dcaf0', label: 'Light Blue' },
    { value: '#212529', label: 'Dark' },
    { value: '#f8f9fa', label: 'Light' }
  ];

  const renderEditableCell = (category, field) => {
    if (editingCell?.categoryId === category.id && editingCell?.field === field) {
      return (
        <div className="d-flex align-items-center">
          {field === 'color' ? (
            <div className="d-flex align-items-center gap-2">
              <InputGroup>
                <Form.Control
                  type="color"
                  name={field}
                  value={category[field]}
                  onChange={(e) => handleCellChange(e, category.id)}
                  style={{ width: '50px', height: '38px', padding: '2px' }}
                />
                <Form.Control
                  type="text"
                  name={field}
                  value={category[field]}
                  onChange={(e) => handleCellChange(e, category.id)}
                  placeholder="#000000"
                  style={{ width: '100px' }}
                />
              </InputGroup>
              <Button
                variant="outline-success"
                size="sm"
                onClick={() => handleSave(category.id)}
              >
                <FontAwesomeIcon icon={faCheck} />
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleCancel(category.id)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          ) : field === 'icon' ? (
            <div className="d-flex align-items-center gap-2">
              {category[field] && (
                <FontAwesomeIcon 
                  icon={iconOptions.find(opt => opt.value === category[field])?.icon || faHome} 
                  style={{ fontSize: '1.2em' }}
                />
              )}
            </div>
          ) : (
            <div className="d-flex align-items-center">
              <Form.Control
                type={field === 'description' ? 'textarea' : 'text'}
                name={field}
                value={category[field] || ''}
                onChange={(e) => handleCellChange(e, category.id)}
                autoFocus
              />
              <Button
                variant="outline-success"
                size="sm"
                className="ms-2"
                onClick={() => handleSave(category.id)}
              >
                <FontAwesomeIcon icon={faCheck} />
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                className="ms-1"
                onClick={() => handleCancel(category.id)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          )}
        </div>
      );
    }
    return (
      <div onClick={() => {
        if (field === 'icon') {
          setSelectedCategory(category);
          setShowIconModal(true);
        } else {
          handleCellClick(category.id, field);
        }
      }} style={{ cursor: 'pointer', textAlign: field === 'color' || field === 'icon' ? 'center' : 'left' }}>
        {field === 'color' ? (
          <div className="d-flex align-items-center justify-content-center gap-2">
            <div 
              style={{ 
                width: '60px', 
                height: '30px', 
                backgroundColor: category[field], 
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: getContrastColor(category[field]),
                fontSize: '0.8em',
                fontWeight: 'bold'
              }} 
            >
              {category[field]}
            </div>
          </div>
        ) : field === 'icon' ? (
          category[field] ? (
            <div className="d-flex justify-content-center">
              <FontAwesomeIcon 
                icon={iconOptions.find(opt => opt.value === category[field])?.icon || faHome} 
                style={{ fontSize: '1.2em' }}
              />
            </div>
          ) : (
            <div className="text-center">-</div>
          )
        ) : (
          category[field] || '-'
        )}
      </div>
    );
  };

  return (
    <div className="category-management">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Manage Categories for Requirements</h2>
        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Add Category
        </Button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <p>Loading categories...</p>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th className="text-center">Name</th>
              <th className="text-center">Description</th>
              <th className="text-center">Colour</th>
              <th className="text-center">Icon</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.map(category => (
              <tr key={category.id}>
                <td>{renderEditableCell(category, 'name')}</td>
                <td>{renderEditableCell(category, 'description')}</td>
                <td>{renderEditableCell(category, 'color')}</td>
                <td>{renderEditableCell(category, 'icon')}</td>
                <td>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDelete(category.id)}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showIconModal} onHide={() => setShowIconModal(false)} onClick={stopPropagation}>
        <Modal.Header closeButton onClick={stopPropagation}>
          <Modal.Title>Select an Icon</Modal.Title>
        </Modal.Header>
        <Modal.Body onClick={stopPropagation}>
          {renderIconGrid()}
        </Modal.Body>
        <Modal.Footer onClick={stopPropagation}>
          <Button variant="secondary" onClick={() => setShowIconModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleIconConfirm}
            disabled={!selectedIcon}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} onClick={stopPropagation}>
        <Modal.Header closeButton onClick={stopPropagation}>
          <Modal.Title>Add New Category</Modal.Title>
        </Modal.Header>
        <Modal.Body onClick={stopPropagation}>
          <Form onClick={stopPropagation}>
            <Form.Group className="mb-3" onClick={stopPropagation}>
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={newCategory.name}
                onChange={(e) => setNewCategory(prev => ({ ...prev, name: e.target.value }))}
                required
                onClick={stopPropagation}
              />
            </Form.Group>

            <Form.Group className="mb-3" onClick={stopPropagation}>
              <Form.Label>Description (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                value={newCategory.description}
                onChange={(e) => setNewCategory(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                onClick={stopPropagation}
              />
            </Form.Group>

            <Form.Group className="mb-3" onClick={stopPropagation}>
              <Form.Label>Color</Form.Label>
              <InputGroup onClick={stopPropagation}>
                <Form.Control
                  type="color"
                  name="color"
                  value={newCategory.color}
                  onChange={(e) => setNewCategory(prev => ({ ...prev, color: e.target.value }))}
                  style={{ width: '50px', height: '38px', padding: '2px' }}
                  onClick={stopPropagation}
                />
                <Form.Control
                  type="text"
                  name="color"
                  value={newCategory.color}
                  onChange={(e) => setNewCategory(prev => ({ ...prev, color: e.target.value }))}
                  placeholder="#000000"
                  onClick={stopPropagation}
                />
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3" onClick={stopPropagation}>
              <Form.Label>Icon (Optional)</Form.Label>
              <div className="d-flex align-items-center gap-2" onClick={stopPropagation}>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCategory({ id: 'new' });
                    setShowIconModal(true);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {newCategory.icon ? (
                    <FontAwesomeIcon 
                      icon={iconOptions.find(opt => opt.value === newCategory.icon)?.icon || faHome} 
                      style={{ fontSize: '1.2em' }}
                    />
                  ) : (
                    <span>Click to select icon</span>
                  )}
                </div>
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer onClick={stopPropagation}>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddCategory}>
            Create
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CategoryManagement;
